import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { takeStashedConsents } from "@/components/ConsentFields";
import { sendSignupAttribution } from "@/lib/attribution";
import { sendSignupSource, resetSaved } from "@/lib/membership";
import { apiLogin, apiRegister, apiMe, apiLogout, apiGoogleSession, setToken, getToken, apiGoogleOAuth } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!getToken()) { setUser(null); setLoading(false); return; }
    try {
      const { user } = await apiMe();
      setUser(user);
    } catch {
      setToken("");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If returning from Emergent OAuth callback, let AuthCallback exchange the session first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { token, user } = await apiLogin({ email, password });
    setToken(token);
    setUser(user);
    return user;
  }, []);

  const register = useCallback(async (first_name, last_name, email, password, consents) => {
    const { token, user } = await apiRegister({ first_name, last_name, email, password, consents });
    setToken(token);
    setUser(user);
    sendSignupAttribution();
    sendSignupSource();
    return user;
  }, []);

  const completeGoogle = useCallback(async (session_id) => {
    const { token, user } = await apiGoogleSession(session_id, takeStashedConsents());
    setToken(token);
    setUser(user);
    sendSignupAttribution();
    sendSignupSource();
    return user;
  }, []);

  const completeGoogleCode = useCallback(async (code, redirectUri) => {
    const { token, user } = await apiGoogleOAuth(code, redirectUri, takeStashedConsents());
    setToken(token);
    setUser(user);
    sendSignupAttribution();
    sendSignupSource();
    return user;
  }, []);

  const logout = useCallback(async () => {
    try { await apiLogout(); } catch {}
    resetSaved();
    setToken("");
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, register, logout, completeGoogle, completeGoogleCode, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
